<script setup>
import { computed } from 'vue'

const props = defineProps({
    href: {
        type: String,
        default: '',
    },
    allowInsecure: {
        type: Boolean,
        default: false,
    },
    to: {
        type: [Object, String],
        default: null,
    },
    name: {
        type: String,
        default: '',
    },
    params: {
        type: Object,
        default: () => ({}),
    },
})

const routerLinkTo = computed(() => {
    if (typeof props.to === 'string') return props.to
    return {
        name: props.name,
        params: props.params,
        ...(props.to || {}),
    }
})

// Perform more complex prop validations than is possible
// inside individual validator functions for each prop.
function validateProps() {
    if (!import.meta.dev) return

    if (props.href) {
        // Check for non-external URL in href.
        if (!/^\w+:/.test(props.href)) {
            return console.warn(
                `Invalid <BaseLink> href: ${props.href}.\nIf you're trying to link to a local URL, provide at least a name or to`
            )
        }
        // Check for insecure URL in href.
        if (!props.allowInsecure && !/^https/.test(props.href)) {
            return console.warn(
                `Insecure <BaseLink> href: ${props.href}.\nWhen linking to external sites, always prefer https URLs. If this site does not offer SSL, explicitly add the allow-insecure attribute on <BaseLink>.`
            )
        }
    } else {
        // Check for insufficient props.
        if (!props.name && !props.to) {
            return console.warn(
                `Invalid <BaseLink> props:\n\n${JSON.stringify(
                    props,
                    null,
                    2
                )}\n\nEither a \`name\` or \`to\` is required for internal links, or an \`href\` for external links.`
            )
        }
    }
}

// Ran in `created()` before; the body of <script setup> is the same point in
// the lifecycle — props are resolved, nothing is mounted yet.
validateProps()
</script>

<template>
    <a
        v-if="href"
        v-bind="$attrs"
        :href="href"
        target="_blank"
    ><slot /></a>
    <router-link
        v-else
        v-bind="$attrs"
        :to="routerLinkTo"
    ><slot /></router-link>
</template>
